import React, { useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { TRootState } from '@store/root-reducer';
import StepButtons from '@components/step-buttons';
import userInterfaceStore from '@store/user-interface';
import PAGE_STYLES from '../../helpers/page-styles';
import TextArea from '@common/components/text-area';
import { COLOR_BLUE_050 } from '@common/assets/styles/colors';

export const RecapSC = styled.div`
  ${PAGE_STYLES};

  .header {
    text-align: center;
    min-height: 20px;

    h4 {
      margin-bottom: 0;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;

    span {
      &.expert-name,
      &.expert-expertise {
        font-size: 14px;
        line-height: 25px;
      }

      &.expert-name {
        font-weight: 600;
      }

      &.expert-expertise {
        text-transform: uppercase;
      }

      &.order-date {
        font-size: 17px;
        line-height: 25px;
        margin-bottom: 8px;
        text-transform: capitalize;
      }

      &.order-time {
        font-weight: 600;
        font-size: 17px;
        line-height: 25px;
        margin-bottom: 8px;
      }

      &.order-note {
        font-size: 14px;
        line-height: 21px;
        margin-bottom: 48px;
        display: block;
        text-align: left;
        padding-left: 20px;
        padding-right: 20px;
      }
    }

    div {
      &.order-place {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-size: 14px;
        line-height: 21px;
        margin-bottom: 8px;
      }
    }

    .title-area {
      text-align: center;

      h4 {
        margin-bottom: 32px;
      }

      span {
        display: block;
        width: 100%;
        text-align: center;
      }

      .tag-name {
        font-weight: 600;
        font-size: 17px;
        line-height: 25px;
        margin-bottom: 8px;
      }
    }

    .note-part {
      margin-top: 32px;
      margin-bottom: 16px;
      width: 100%;

      label {
        display: block;
        margin-bottom: 8px;
      }

      textarea {
        display: block;
        height: 80px;

        ::placeholder {
          color: ${COLOR_BLUE_050};
        }

        ::-webkit-resizer {
          display: none;
        }
      }
    }
  }
`;

const OrderRecap = () => {
  const order = useSelector((store: TRootState) => store.userInterface.order);
  const dispatch = useDispatch();

  const {
    expert: { name: expertName },
    datetime: { year, month, day, time },
    expertise: { name: expertiseName },
    medicalInstitution: {
      name: medicalInstitutionName,
      address: medicalInstitutionAddress,
    },
    note,
  } = order;

  const date = `${year}-${month}-${day}`;

  const tagName = useMemo(() => {
    if (order.event?.tag) {
      return order.event.tag.name || '';
    }
    return null;
  }, [order]);

  const handleNoteChange = (e: React.FormEvent<HTMLTextAreaElement>) => {
    dispatch(userInterfaceStore.setNote({ note: e.currentTarget.value }));
  };

  return (
    <RecapSC>
      <div className="header">
        <h4>Zhrnutie rezervácie</h4>
      </div>
      <div className="content">
        <div className={'title-area'}>
          {tagName ? <div className={'tag-name'}>{tagName}</div> : null}
          <span className="order-date">
            {moment(`${date} ${time}`).format('dddd Do MMMM YYYY')}
          </span>
        </div>
        <span className="order-time">
          {moment(`${date} ${time}`).format('HH:mm')} hod
        </span>

        <div className="order-place">
          <span className="expert-name">{expertName}</span>
          <span className="order-place__name">{medicalInstitutionName}</span>
          <span className="order-place__address">
            {medicalInstitutionAddress}
          </span>
        </div>
        <span className="expert-expertise">{expertiseName}</span>
        <div className="note-part">
          <label>Poznámka</label>
          <TextArea
            placeholder={'Sem vpíšte poznámku...'}
            value={note ?? undefined}
            onChange={handleNoteChange}
          >
            {note}
          </TextArea>
        </div>
      </div>
      <div className={'footer'}>
        <StepButtons
          renderNextBtn={true}
          handleNextClick={() => dispatch(userInterfaceStore.moveToNextStep())}
          handlePrevClick={() => {
            dispatch(userInterfaceStore.moveToPrevStep());
            dispatch(userInterfaceStore.resetEventId());
          }}
        />
      </div>
    </RecapSC>
  );
};

export default OrderRecap;
